import React, { FC, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiUrl } from '../utils/api';

const PlayerPage: FC = () => {
  const location = useLocation();
  const recordingId = (location.pathname.match(/^\/streams\/recording\/(.+)$/)?.[1]) || '';
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = user?.access_token;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const load = async () => {
      if (!recordingId) {
        console.warn('[RecordingPlayer] No recordingId in path', { path: location.pathname });
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const requestUrl = apiUrl(`/recordings/${recordingId}`);
        console.log('[RecordingPlayer] Fetching recording', { recordingId, url: requestUrl, hasToken: !!token });
        const res = await fetch(requestUrl, {
          method: 'GET',
          headers: {
            Accept: 'video/mp4',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        console.log('[RecordingPlayer] Response', { status: res.status, ok: res.ok, contentType: res.headers.get('Content-Type') });
        if (!res.ok) throw new Error(`Failed to load recording: ${res.status}`);
        const blob = await res.blob();
        console.log('[RecordingPlayer] Blob ready', { size: blob.size });
        const objectUrl = URL.createObjectURL(blob);
        setVideoUrl(objectUrl);
        // Autoplay when ready
        setTimeout(() => {
          const v = videoRef.current;
          if (v) {
            v.load();
            v.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
          }
        }, 50);
      } catch (e: any) {
        setError(e?.message || 'Не удалось загрузить запись');
        console.error('[RecordingPlayer] Fetch error', e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
    return () => {
      if (videoUrl) {
        try { URL.revokeObjectURL(videoUrl); } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingId, token, location.pathname]);

  const onTogglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setIsPlaying(true)).catch(() => {});
    } else {
      v.pause();
      setIsPlaying(false);
    }
  };

  const onSeek = (value: number) => {
    const v = videoRef.current;
    if (!v) return;
    const newTime = Math.min(Math.max(value, 0), duration || 0);
    v.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const fmtTime = (sec: number) => {
    if (!Number.isFinite(sec)) return '0:00';
    const s = Math.floor(sec % 60);
    const m = Math.floor(sec / 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 105px)', padding: 16 }}>
      <div style={{ marginBottom: 12 }}>
        <button
          onClick={() => navigate('/streams')}
          style={{
            background: '#424F67',
            color: '#fff',
            border: '1px solid #2E3749',
            borderRadius: 8,
            padding: '8px 12px',
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          ← Назад к потокам
        </button>
      </div>

      <div style={{ flex: 1, background: '#1F2534', border: '1px solid #2E3749', borderRadius: 12, boxShadow: '0 2px 16px #0000003D', padding: 12, display: 'flex', flexDirection: 'column' }}>
        <div style={{ position: 'relative', flex: 1, borderRadius: 8, overflow: 'hidden' }}>
          <video
            ref={videoRef}
            src={videoUrl || undefined}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            controls={false}
            onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
            onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
            onEnded={() => setIsPlaying(false)}
          />
          {error && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', background: 'rgba(0,0,0,0.2)' }}>
              {error}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#1F2534', border: '1px solid #2E3749', boxShadow: '0 2px 16px #0000003D', padding: '10px 12px', borderRadius: 8, marginTop: 12 }}>
          <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }} onClick={onTogglePlay} disabled={!videoUrl || isLoading}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#E95A2B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {isPlaying ? (
                <div style={{ display: 'flex', gap: 4 }}>
                  <div style={{ width: 4, height: 12, background: '#1F2534' }} />
                  <div style={{ width: 4, height: 12, background: '#1F2534' }} />
                </div>
              ) : (
                <div style={{ width: 0, height: 0, borderTop: '7px solid transparent', borderBottom: '7px solid transparent', borderLeft: '12px solid #1F2534', marginLeft: 2 }} />
              )}
            </div>
          </button>
          <span style={{ color: '#fff', fontSize: 12, minWidth: 40, textAlign: 'center' }}>{fmtTime(currentTime)}</span>
          <div style={{ flex: 1 }}>
            <input
              type="range"
              min={0}
              max={Math.max(duration, 0) || 0}
              step={0.1}
              value={Math.min(currentTime, duration || 0)}
              onChange={(e) => onSeek(Number(e.target.value))}
              style={{ width: '100%', appearance: 'none', height: 6, borderRadius: 3, background: '#2D3A54', outline: 'none' }}
            />
          </div>
          <span style={{ color: '#fff', fontSize: 12, minWidth: 40, textAlign: 'center' }}>{fmtTime(duration)}</span>
        </div>
      </div>
    </div>
  );
};

export default PlayerPage;
